import { thingsboardService } from './thingsboard';

export type SubscriptionCallback = (data: any) => void;

interface Subscription {
  id: number;
  entityType: 'DEVICE' | 'ASSET';
  entityId: string;
  keys?: string;
  scope?: string;
  type: 'telemetry' | 'alarm';
  callback: SubscriptionCallback;
}

class TbWebSocketManager {
  private ws: WebSocket | null = null;
  private subscriptions: Map<number, Subscription> = new Map();
  private nextCmdId = 1;
  private isConnecting = false;
  private reconnectTimer: any = null;
  private reconnectAttempts = 0;
  private urlGetter: () => string;
  private name: string;

  constructor(name: string, urlGetter: () => string) {
    this.name = name;
    this.urlGetter = urlGetter;
  }

  async connect() {
    if (this.ws?.readyState === WebSocket.OPEN) return;
    if (this.isConnecting) return;
    this.isConnecting = true;

    try {
      let token = thingsboardService.getJwtToken();
      // If token is missing, or we are reconnecting (suggesting token might be the issue)
      if (!token || this.reconnectAttempts > 0) {
        console.log(`[WebSocket ${this.name}] Token missing or reconnecting. Attempting to get fresh token...`);
        // Try refreshing first
        token = await thingsboardService.refreshJwtToken();
        
        // Fallback to login if refresh fails and we need a token
        if (!token) {
          token = await thingsboardService.login(undefined, undefined, this.reconnectAttempts > 0);
        }
      }
      if (!token) throw new Error('No JWT token available');

      const url = this.urlGetter();
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log(`[WebSocket ${this.name}] Connected`);
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.resubscribeAll();
      };

      this.ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        // Log incoming messages for debugging
        //console.log(`[WebSocket ${this.name}] Received:`, message);

        if (message.errorCode) {
          //console.error(`[WebSocket ${this.name}] Error from server:`, message.errorMsg);
        }

        const subId = message.subscriptionId || message.cmdId;
        if (subId && this.subscriptions.has(subId)) {
          const sub = this.subscriptions.get(subId);
          if (sub) {
             if (sub.type === 'telemetry') {
               // Telemetry updates are in message.data
               if (message.data) sub.callback(message.data);
             } else if (sub.type === 'alarm') {
               // Alarm updates can be the whole message or message.update
               sub.callback(message.update || message.data || message);
             }
          }
        }
      };

      this.ws.onclose = () => {
          //console.log(`[WebSocket ${this.name}] Disconnected`);
        this.isConnecting = false;
        this.handleReconnect();
      };

      this.ws.onerror = (error) => {
        //console.error(`[WebSocket ${this.name}] Error`, error);
        this.ws?.close();
      };

    } catch (error) {
      //console.error(`[WebSocket ${this.name}] Failed to connect`, error);
      this.isConnecting = false;
      this.handleReconnect();
    }
  }

  private handleReconnect() {
    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
    //console.log(`[WebSocket ${this.name}] Reconnecting in ${delay}ms`);
    
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private resubscribeAll() {
    for (const sub of this.subscriptions.values()) {
      this.sendSubscriptionCmd(sub);
    }
  }

  private sendSubscriptionCmd(sub: Subscription, unsubscribe: boolean = false) {
    if (this.ws?.readyState !== WebSocket.OPEN) return;

    if (sub.type === 'telemetry') {
      const cmd = {
        tsSubCmds: [
          {
            entityType: sub.entityType,
            entityId: sub.entityId,
            scope: sub.scope || 'LATEST_TELEMETRY',
            cmdId: sub.id,
            keys: sub.keys,
            unsubscribe: unsubscribe
          }
        ]
      };
      //console.log(`[WebSocket ${this.name}] Sending tsSubCmds:`, JSON.stringify(cmd));
      this.ws.send(JSON.stringify(cmd));
    } else if (sub.type === 'alarm') {
      const authCmd = {
        cmdId: 0,
        token: thingsboardService.getJwtToken()
      };

      if (unsubscribe) {
        const cmd = {
          type: "ALARM_DATA",
          unsubscribe: true,
          cmdId: sub.id
        };
        //console.log(`[WebSocket ${this.name}] Sending ALARM_DATA unsubscribe:`, JSON.stringify(cmd));
        this.ws.send(JSON.stringify(cmd));
      } else {
        const cmds = {cmds:[{
          type: "ALARM_DATA",
          query: {
            entityFilter: {
              type: 'singleEntity',
              singleEntity: {
                id: sub.entityId,
                entityType: 'CUSTOMER'
              }
            },
            pageLink: {
              assigneeId: null,
              pageSize: 20,
              page: 0,
              textSearch: null,
              searchPropagatedAlarms: true,
              statusList: ['ACTIVE', 'ACK', 'UNACK'],
              severityList: ['CRITICAL', 'MAJOR', 'MINOR', 'WARNING', 'INDETERMINATE'],
              typeList: [],
              sortOrder: {
                key: { type: 'ALARM_FIELD', key: 'createdTime' },
                direction: 'DESC'
              },
              timeWindow: 2592000000 // 30 days
            },
            alarmFields: [
              { type: "ALARM_FIELD", key: "createdTime" },
              { type: "ALARM_FIELD", key: "originatorDisplayName" },
              { type: "ALARM_FIELD", key: "type" },
              { type: "ALARM_FIELD", key: "severity" },
              { type: "ALARM_FIELD", key: "status" },
              { type: "ALARM_FIELD", key: "assignee" }
            ],
            entityFields: [],
            latestValues: []
          },
          cmdId: sub.id
        }]};
        //console.log(`[WebSocket ${this.name}] Sending ALARM_DATA sub for customer ${sub.entityId}`, cmds);
        this.ws.send(JSON.stringify(cmds));
      }
    }
  }

  subscribeTelemetry(entityType: 'DEVICE' | 'ASSET', entityId: string, keys: string, callback: SubscriptionCallback): number {
    const subId = this.nextCmdId++;
    const subscription: Subscription = {
      id: subId,
      entityType,
      entityId,
      keys,
      type: 'telemetry',
      scope: 'LATEST_TELEMETRY',
      callback
    };
    
    this.subscriptions.set(subId, subscription);
    
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.sendSubscriptionCmd(subscription);
    } else {
      this.connect();
    }

    return subId;
  }

  subscribeAlarms(customerId: string, callback: SubscriptionCallback): number {
    const subId = this.nextCmdId++;
    const subscription: Subscription = {
      id: subId,
      entityType: 'ASSET', // dummy, alarmDataCmds for CUSTOMER uses ALARM_DATA
      entityId: customerId, 
      type: 'alarm',
      callback
    };
    
    this.subscriptions.set(subId, subscription);
    
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.sendSubscriptionCmd(subscription);
    } else {
      this.connect();
    }

    return subId;
  }

  unsubscribe(subId: number) {
    const sub = this.subscriptions.get(subId);
    if (sub) {
      this.sendSubscriptionCmd(sub, true);
      this.subscriptions.delete(subId);
    }
  }
}

export const wsManager = new TbWebSocketManager('Telemetry', () => thingsboardService.getWsUrl());
export const alarmWsManager = new TbWebSocketManager('Alarm', () => thingsboardService.getAlarmWsUrl());
